import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Steps } from './DialogStepsEnum';
import { IFinancingApplication } from '../../../Types';
import { verifyPreActivationStepData } from '../../../utils';
import Button from '../../BuildingBlocks/Button/Button';
import QuestionsStep from './QuestionsStep';
import ConnectBankStep from './ConnectBankStep';
import DialogSuccessMsg from './DialogSuccessMsg';

interface DialogProps {
  plan: IFinancingApplication;
  startOpen?: boolean;
  alreadySubmitted?: boolean;
}

export default function PlanActivationDialog({
  plan,
  startOpen = false,
  alreadySubmitted = false,
}: DialogProps) {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectedFromBankConnection =
    params.get('self_activated_plan') === plan.id;

  let startingStep = Steps.closed;
  if (startOpen) {
    startingStep = Steps.questions;
  } else if (redirectedFromBankConnection) {
    startingStep = Steps.ops_manual_activation_msg;
  }
  const [step, setStep] = useState<Steps>(startingStep);

  const { preActivationSteps = {} } = plan;
  const { bank_connected, other_loans, active_loans } = preActivationSteps;
  const hasBankAccountConnected = verifyPreActivationStepData(
    bank_connected,
    true,
  );
  const hasNoLoans =
    verifyPreActivationStepData(other_loans, true) &&
    verifyPreActivationStepData(active_loans, true);

  const closeDialog = () => {
    setStep(Steps.closed);
  };
  const dismissDialog = () => {
    setStep(Steps.done);
    if (redirectedFromBankConnection) {
      params.delete('self_activated_plan');
      history.replace({
        search: params.toString(),
      });
    }
  };
  const goToQuiestionsStep = () => setStep(Steps.questions);
  const goToBankStep = () => setStep(Steps.connect_bank);
  const goToManualActivationMsg = () =>
    setStep(Steps.ops_manual_activation_msg);
  // const goToActivationSuccessMsg = () => setStep(Steps.activation_success_msg);
  // Temporary - when plan would be activated automatically - we should show activation_success_msg
  const goToActivationSuccessMsg = () =>
    setStep(Steps.ops_manual_activation_msg);

  if (alreadySubmitted && !redirectedFromBankConnection) {
    return null;
  }

  return (
    <>
      {step === Steps.closed && (
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={goToQuiestionsStep}
        >
          Activate Plan
        </Button>
      )}
      {step === Steps.questions && (
        <QuestionsStep
          hasLoans={!hasNoLoans}
          hasBankAccount={hasBankAccountConnected}
          goToManualActivationMsg={goToManualActivationMsg}
          goToActivationSuccessMsg={goToActivationSuccessMsg}
          goToBankStep={goToBankStep}
          closeDialog={closeDialog}
          planId={plan.id}
        />
      )}
      {step === Steps.connect_bank && (
        <ConnectBankStep
          goBack={goToQuiestionsStep}
          goNext={goToManualActivationMsg}
        />
      )}
      {step === Steps.ops_manual_activation_msg && (
        <DialogSuccessMsg
          closeDialog={dismissDialog}
          title="Thanks for your response!"
          text="Our team will be in touch within the next 3 business days."
          iconPath="/plan/activationWaitIcon.svg"
        />
      )}
      {step === Steps.activation_success_msg && (
        <DialogSuccessMsg
          closeDialog={dismissDialog}
          title="Woohoo - your Growth Plan is now active!"
          text={`We're excited to help you get growing.`}
        />
      )}
    </>
  );
}
